import { devWarn } from './devLog';

export const getSearchCategory = (type = '') => {
  const normalized = String(type || '').toLowerCase();
  if (normalized.includes('villa')) return 'villa';
  if (normalized.includes('plot')) return 'plot';
  return 'apartment';
};

const normalizeKind = (kind = 'location') =>
  String(kind || 'location').toLowerCase();

export const normalizeQueryPayload = (payload = {}, searchType = 'apartment_buy') => {
  const label = payload.label || payload.query || '';
  const query = payload.query || payload.label || '';
  const kind = normalizeKind(payload.kind);
  const slug = payload.slug || null;

  if (!label || !query) return null;

  const desiredCategory = getSearchCategory(searchType);
  const candidateType = payload.propertyType || searchType;
  const candidateCategory = getSearchCategory(candidateType);

  const propertyType =
    candidateCategory === desiredCategory ? candidateType : searchType;

  return {
    label,
    query,
    kind,
    propertyType,
    slug,
  };
};

const isRentType = (searchType = '') =>
  String(searchType || '').toLowerCase().includes('_rent') ||
  String(searchType || '').toLowerCase().includes('-rent');

const getSearchPathForType = (searchType = 'apartment_buy') => {
  if (isRentType(searchType)) return '/rents-location-page';
  const cat = getSearchCategory(searchType);
  if (cat === 'villa') return '/villas-search-screen';
  if (cat === 'plot') return '/plots-search-screen';
  return '/apts-search-screen';
};

export const getMapPathForType = (searchType = 'apartment_buy') => {
  if (isRentType(searchType)) return '/rents-location-page-map2';
  const cat = getSearchCategory(searchType);
  if (cat === 'villa') return '/villas-location-page-map2';
  if (cat === 'plot') return '/plots-location-page-map2';
  return '/apts-location-page';
};

export const buildMultiSearchUrl = (queries = [], searchType = 'apartment_buy', currentParams = {}) => {
  if (!queries.length) return null;
  const path = getSearchPathForType(searchType);
  const normalized = queries
    .map((q) => normalizeQueryPayload(q, searchType))
    .filter((entry) => entry && (entry.kind === 'location' || entry.kind === 'city' || entry.kind === 'builder' || entry.kind === 'project'));
  if (!normalized.length) return null;

  const cityChip = normalized.find(q => q.kind === 'city');
  const locationChip = normalized.find(q => q.kind === 'location');
  const otherChip = normalized.find(q => q.kind !== 'city' && q.kind !== 'location');

  const params = new URLSearchParams();
  params.set('type', searchType);

  if (cityChip) params.set('city', cityChip.query);
  else if (currentParams.city) params.set('city', currentParams.city);
  if (locationChip) params.set('location', locationChip.query);
  else if (!cityChip && otherChip) {
    if (otherChip.kind === 'builder') params.set('builder', otherChip.query);
    else if (otherChip.kind === 'project' && otherChip.slug) params.set('location', otherChip.label || otherChip.query);
  }
  if (currentParams.categoryname) params.set('categoryname', currentParams.categoryname);

  params.set('multi', encodeURIComponent(JSON.stringify(normalized)));
  return `${path}?${params.toString()}`;
};

export const buildMultiMapUrl = (queries = [], searchType = 'apartment_buy', currentParams = {}) => {
  if (!queries.length) return null;
  const path = getMapPathForType(searchType);
  const normalizedAll = queries
    .map((q) => normalizeQueryPayload(q, searchType))
    .filter((entry) => entry && (entry.kind === 'location' || entry.kind === 'city' || entry.kind === 'builder' || entry.kind === 'project'));
  if (!normalizedAll.length) return null;

  /* Map view supports a single location search at a time */
  const locationChips = normalizedAll.filter((q) => q.kind === 'location');
  const primaryLocation = locationChips[0];
  const normalized = [
    ...normalizedAll.filter((q) => q.kind !== 'location'),
    ...(primaryLocation ? [primaryLocation] : []),
  ];
  if (!normalized.length) return null;

  const cityChip = normalized.find(q => q.kind === 'city');
  const locationChip = normalized.find(q => q.kind === 'location');
  const otherChip = normalized.find(q => q.kind !== 'city' && q.kind !== 'location');

  const params = new URLSearchParams();
  params.set('type', searchType);

  if (cityChip) params.set('city', cityChip.query);
  if (locationChip) params.set('location', locationChip.query);
  else if (!cityChip && otherChip) {
    if (otherChip.kind === 'builder') params.set('builder', otherChip.query);
  }

  params.set('multi', encodeURIComponent(JSON.stringify(normalized)));

  // Preserve other params from current URL
  Object.keys(currentParams).forEach(key => {
    if (!['type', 'location', 'city', 'multi', 'builder', '_map_refresh'].includes(key) && currentParams[key]) {
      params.set(key, currentParams[key]);
    }
  });

  return `${path}?${params.toString()}`;
};

export const decodeMultiParam = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) =>
        normalizeQueryPayload(
          {
            label: item?.label,
            query: item?.query,
            kind: item?.kind,
            propertyType: item?.propertyType,
            slug: item?.slug,
          },
          item?.propertyType
        )
      )
      .filter((entry) => entry && (entry.kind === 'location' || entry.kind === 'city'));
  } catch (err) {
    devWarn('Failed to decode multi param', err);
    return [];
  }
};

export const stripChipMeta = (chips = []) =>
  chips
    .map(({ id, ...rest }) => rest)
    .map((entry) => normalizeQueryPayload(entry, entry.propertyType))
    .filter((entry) => entry && (entry.kind === 'location' || entry.kind === 'city'));

export const collectProjects = (payload) => {
  if (!payload) return [];
  const pools = Object.keys(payload).filter((key) =>
    /^allprojects/i.test(key)
  );
  const bucket = new Map();
  pools.forEach((key) => {
    (payload[key] || []).forEach((project) => {
      const id = project?.id || project?.slug || project?.title;
      if (!bucket.has(id)) bucket.set(id, project);
    });
  });
  return Array.from(bucket.values());
};

